/**
 * Cache de quadros em DOIS NÍVEIS + filas com prioridade.
 *
 * Nível 1 — comprimido: todo quadro do plano é baixado com `fetch` (CORS, sem
 * credenciais — equivalente a `crossOrigin: 'anonymous'`, então o canvas do
 * mapa continua exportável) e guardado como Blob (JPEG). É barato: 24 h em
 * 3600 px ≈ 490 MB, e o navegador pode manter blobs grandes fora da memória.
 *
 * Nível 2 — decodificado: só uma JANELA de quadros (os próximos na ordem de
 * reprodução) fica decodificada como ImageBitmap (`createImageBitmap(blob)`,
 * fora da thread principal, 2–3 por vez). Ao sair da janela o bitmap é
 * fechado (`close()`) e o blob continua — decodificar de novo é rápido e não
 * usa a rede.
 *
 * Estados: queued → loading → downloaded ⇄ decoding → ready; ou error.
 * A imagem na tela é "fixada" (pin) e nunca é liberada enquanto aparece.
 *
 * Com um depósito em DISCO (`setStore`, ver diskStore.ts), o nível 1 sai da
 * RAM: o arquivo baixado é gravado no disco e o Blob é solto; para decodificar,
 * relê do disco. Antes de ir à rede, o disco é consultado — o que ficou da
 * visita anterior não é baixado de novo. Se o navegador apagar um arquivo, o
 * quadro volta para a fila e é baixado outra vez.
 *
 * Sem `createImageBitmap` (navegadores antigos) a decodificação usa `<img>`
 * a partir do blob (URL de objeto, mesma origem — o canvas continua limpo).
 */
import { abortError, fetchWithRetry, HttpError, isAbortError, TimeoutError } from '../lib/fetchRetry';
import type { DecodeRequest, DecodeResponse } from './decode.worker';
import type { BlobStore } from './diskStore';
import type { ImageWidth } from './star';

export type FrameImage = ImageBitmap | HTMLImageElement;
export type FrameStatus = 'queued' | 'loading' | 'downloaded' | 'decoding' | 'ready' | 'error';

export interface FrameRequest<W extends number = ImageWidth> {
  url: string;
  productId: string;
  time: number;
  width: W;
  /** Ordem de download (menor = antes). */
  priority: number;
  /** Deve ficar decodificado (janela), com esta prioridade (menor = antes); null/ausente = não. */
  decode?: number | null;
  /**
   * Largura do BITMAP decodificado (px), quando a tela não precisa do arquivo
   * inteiro (ver `decodeWidthFor` em budget.ts); null/ausente = tamanho natural.
   * Um quadro já pronto noutra largura continua na tela até a nova versão chegar.
   */
  decodeWidth?: number | null;
}

/** De qual quadro é o arquivo que está sendo decodificado. */
export interface DecodeContext {
  url: string;
  productId: string;
}

/** Como baixar e decodificar (injetável nos testes). */
export interface FrameLoader {
  download(url: string, signal: AbortSignal): Promise<Blob>;
  /**
   * `resizeWidth`: decodificar já reduzido a esta largura (null = natural).
   * `context`: de qual quadro é o arquivo — o radar precisa saber de qual
   * estação ele é para reprojetar; o GOES ignora.
   */
  decode(blob: Blob, resizeWidth?: number | null, context?: DecodeContext): Promise<FrameImage>;
}

interface Entry<W extends number = ImageWidth> {
  url: string;
  productId: string;
  time: number;
  width: W;
  priority: number;
  /** Prioridade na janela de decodificação; null = fora da janela. */
  decodePriority: number | null;
  status: FrameStatus;
  /** Arquivo na RAM (sem depósito em disco, ou enquanto é gravado nele). */
  blob: Blob | null;
  /** Arquivo guardado no depósito em disco. */
  onDisk: boolean;
  image: FrameImage | null;
  /** Tamanho do arquivo (bytes) depois de baixado. */
  bytes: number;
  controller: AbortController | null;
  attempts: number;
  retryAt: number;
  pins: number;
  /** Está no plano atual (último `sync`)? Fora dele, sai assim que deixar a tela. */
  wanted: boolean;
  /** Largura de bitmap pedida (null = natural). */
  decodeWidth: number | null;
  /** Largura de bitmap com que `image` foi decodificada (null = natural). */
  decodedFor: number | null;
  /** Há uma redecodificação em andamento (a imagem atual continua válida). */
  redecoding: boolean;
  /** Geração da decodificação em andamento (resultados antigos são descartados). */
  decodeSeq: number;
}

/** Memória por largura (ver `statsByWidth`). */
export interface WidthStats {
  /** Entradas no cache (qualquer estado). */
  count: number;
  /** Arquivos baixados guardados. */
  downloaded: number;
  /** Bytes comprimidos guardados. */
  bytes: number;
  /** Imagens decodificadas. */
  decoded: number;
  /** Pixels decodificados em memória. */
  pixels: number;
}

export interface FrameCacheStats {
  entries: number;
  /** Quadros com o arquivo baixado (nível 1). */
  downloaded: number;
  /** Quadros decodificados (nível 2). */
  ready: number;
  loading: number;
  decoding: number;
  /** Bytes comprimidos guardados. */
  bytes: number;
  /** Desses, quantos estão na RAM (o resto está no disco). */
  memoryBytes: number;
  /** Pixels decodificados em memória. */
  pixels: number;
}

const MAX_ATTEMPTS = 3;
const RETRY_DELAYS = [1500, 5000];

/**
 * Espelho dos arquivos inteiros (ver detalhe/espelho.ts): o mesmo arquivo,
 * byte a byte, num servidor mais rápido. Só o download muda de endereço — a
 * chave do cache e o depósito em disco continuam com a URL do STAR, e qualquer
 * falha do espelho (404, 5xx, rede, demora) cai no STAR naquele pedido.
 */
export interface EspelhoDeDownload {
  /** Endereço no espelho, ou null para ir direto à URL original. */
  endereco(url: string): string | null;
  /** Resultado de cada pedido ao espelho (null = deu certo). */
  registrar(erro: unknown | null): void;
}

let espelho: EspelhoDeDownload | null = null;

/** Liga (ou desliga, com null) o espelho dos downloads. */
export function definirEspelho(e: EspelhoDeDownload | null): void {
  espelho = e;
}

/**
 * Espera pelo cabeçalho do espelho antes de desistir dele (uma conexão presa
 * cai no STAR em segundos) e, depois, pelo corpo (folgado: conexão lenta).
 */
export const ESPELHO_CABECALHO_MS = 6000;
const ESPELHO_CORPO_MS = 90_000;

async function baixarDoEspelho(url: string, signal: AbortSignal): Promise<Blob> {
  const controller = new AbortController();
  const cancelar = () => controller.abort();
  signal.addEventListener('abort', cancelar, { once: true });
  let prazoMs = ESPELHO_CABECALHO_MS;
  let prazo = setTimeout(cancelar, prazoMs);
  try {
    const r = await fetch(url, { mode: 'cors', credentials: 'omit', signal: controller.signal });
    clearTimeout(prazo);
    if (!r.ok) throw new HttpError(r.status, url);
    prazoMs = ESPELHO_CORPO_MS;
    prazo = setTimeout(cancelar, prazoMs);
    return await r.blob();
  } catch (e) {
    if (signal.aborted) throw abortError();
    // Cancelado pelo prazo: é demora do espelho, não do usuário.
    if (isAbortError(e)) throw new TimeoutError(url, prazoMs);
    throw e;
  } finally {
    clearTimeout(prazo);
    signal.removeEventListener('abort', cancelar);
  }
}

async function downloadBlob(url: string, signal: AbortSignal): Promise<Blob> {
  const e = espelho;
  const alternativo = e?.endereco(url) ?? null;
  if (e && alternativo) {
    try {
      const blob = await baixarDoEspelho(alternativo, signal);
      e.registrar(null);
      return blob;
    } catch (erro) {
      if (isAbortError(erro) && signal.aborted) throw erro;
      e.registrar(erro);
      // Segue para o STAR.
    }
  }
  // O corpo é lido sob o mesmo cancelamento/tempo-limite: um quadro descartado
  // libera a vaga na hora e não continua baixando.
  return fetchWithRetry(url, { mode: 'cors', credentials: 'omit' }, { retries: 0, signal, timeoutMs: 90_000, read: (r) => r.blob() });
}

const objectUrls = new WeakMap<HTMLImageElement, string>();

async function decodeWithImageElement(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
  objectUrls.set(img, url);
  return img;
}

const HAS_BITMAP = typeof createImageBitmap === 'function';

// ---------------------------------------------------------------------------
// Decodificação em workers (ver decode.worker.ts)
// ---------------------------------------------------------------------------

/**
 * Workers de decodificação (a reamostragem é síncrona dentro de cada um): um
 * por par de núcleos, de 2 a 4. Decodificar 7200 px leva ~250 ms; com 2
 * workers a animação nessa largura ficava presa em ~8 quadros/s. Tela de
 * toque fica em 2 — cada decodificação de 7200 segura 124 MB enquanto dura.
 */
export function decodeWorkerCount(cores: number | undefined, touch: boolean): number {
  if (touch) return 2;
  return Math.max(2, Math.min(4, Math.floor((cores ?? 4) / 2)));
}

const DECODE_WORKERS = decodeWorkerCount(
  typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined,
  typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 1,
);

interface DecodePool {
  workers: Worker[];
  pending: Map<number, { resolve: (img: ImageBitmap) => void; reject: (e: unknown) => void }>;
  next: number;
  seq: number;
}

let pool: DecodePool | null | undefined;

function getDecodePool(): DecodePool | null {
  if (pool !== undefined) return pool;
  if (typeof Worker === 'undefined') return (pool = null);
  try {
    const p: DecodePool = { workers: [], pending: new Map(), next: 0, seq: 0 };
    for (let i = 0; i < DECODE_WORKERS; i++) {
      const w = new Worker(new URL('./decode.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (e: MessageEvent<DecodeResponse>) => {
        const req = p.pending.get(e.data.id);
        if (!req) return;
        p.pending.delete(e.data.id);
        if (e.data.ok) req.resolve(e.data.image);
        else req.reject(new Error(e.data.error));
      };
      w.onerror = () => {
        // Worker indisponível (ex.: versão nova publicada): quem espera recebe
        // erro e o cache volta a decodificar na thread principal.
        for (const req of p.pending.values()) req.reject(new Error('worker de decodificação indisponível'));
        p.pending.clear();
        for (const x of p.workers) x.terminate();
        pool = null;
      };
      p.workers.push(w);
    }
    return (pool = p);
  } catch {
    return (pool = null);
  }
}

function decodeInWorker(blob: Blob, resizeWidth: number | null): Promise<ImageBitmap> | null {
  const p = getDecodePool();
  if (!p) return null;
  const id = ++p.seq;
  const w = p.workers[p.next++ % p.workers.length];
  return new Promise<ImageBitmap>((resolve, reject) => {
    p.pending.set(id, { resolve, reject });
    const req: DecodeRequest = { id, blob, resizeWidth };
    w.postMessage(req);
  });
}

/**
 * Carregador padrão: fetch → Blob; createImageBitmap num worker (a redução do
 * 7200 px acontece lá), na thread principal se não houver worker, ou `<img>`
 * como reserva em navegadores antigos.
 */
export const defaultFrameLoader: FrameLoader = {
  download: downloadBlob,
  decode: async (blob, resizeWidth = null) => {
    if (!HAS_BITMAP) return decodeWithImageElement(blob);
    const inWorker = decodeInWorker(blob, resizeWidth);
    if (inWorker) {
      try {
        return await inWorker;
      } catch {
        // Falha no worker: tenta na thread principal (abaixo).
      }
    }
    // `resizeQuality: 'high'` = reamostragem equivalente à redução ao desenhar.
    if (resizeWidth) return createImageBitmap(blob, { resizeWidth, resizeQuality: 'high' });
    return createImageBitmap(blob);
  },
};

/** Reserva com `<img>` + `img.decode()` (usada também em medições). */
export const imageElementFrameLoader: FrameLoader = { download: downloadBlob, decode: decodeWithImageElement };

function releaseImage(image: FrameImage | null): void {
  if (!image) return;
  if ('close' in image) {
    image.close();
    return;
  }
  const url = objectUrls.get(image);
  if (url) URL.revokeObjectURL(url);
}

const frameKey = (productId: string, time: number) => `${productId}|${time}`;
const isDownloaded = (s: FrameStatus) => s === 'downloaded' || s === 'decoding' || s === 'ready';
/** Estado de um quadro sem bitmap: baixado se o arquivo está na RAM ou no disco. */
const idleStatus = (e: Entry<number>): FrameStatus => (e.blob || e.onDisk ? 'downloaded' : 'queued');

export class FrameCache<W extends number = ImageWidth> {
  /** (Aberto em runtime para os scripts de verificação.) */
  readonly entries = new Map<string, Entry<W>>();
  private readonly byFrame = new Map<string, Set<Entry<W>>>();
  private readonly listeners = new Set<() => void>();
  private activeDownloads = 0;
  private activeDecodes = 0;
  private notifyQueued = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * O carregador reduz ao decodificar (`decodeWidth`)? Descoberto na primeira
   * decodificação reduzida: se o bitmap voltar no tamanho natural, paramos de pedir.
   */
  private resizeOk: boolean | null = null;
  /** Depósito em disco do nível 1 (null = arquivos na RAM). */
  private store: BlobStore | null = null;
  /** Avisado quando o disco recusa um arquivo (falta de espaço). */
  private onStoreFull: ((e: unknown) => void) | null = null;

  constructor(
    private readonly maxDownloads = 6,
    private readonly maxDecodes = 3,
    private loader: FrameLoader = defaultFrameLoader,
  ) {}

  setLoader(loader: FrameLoader): void {
    this.loader = loader;
  }

  /**
   * Passa a guardar os arquivos baixados no disco (`store`), soltando-os da
   * RAM. `onFull` é avisado quando o disco recusa um arquivo — ele fica na RAM
   * e quem chamou deve reduzir o orçamento.
   */
  setStore(store: BlobStore | null, onFull: ((e: unknown) => void) | null = null): void {
    this.store = store;
    this.onStoreFull = onFull;
  }

  /** O quadro está no cache (desejado ou na tela)? A faxina do disco não apaga esses. */
  has(url: string): boolean {
    return this.entries.has(url);
  }

  /**
   * Define os quadros desejados. Os que saíram (e não estão na tela) são
   * cancelados e descartados. Dos desejados, os com `decode` ficam
   * decodificados; os demais têm o bitmap fechado (o blob fica).
   */
  sync(requests: readonly FrameRequest<W>[]): void {
    const wanted = new Map<string, { r: FrameRequest<W>; decode: number | null; decodeWidth: number | null }>();
    for (const r of requests) {
      const d = r.decode ?? null;
      const dw = this.resizeOk === false ? null : (r.decodeWidth ?? null);
      const prev = wanted.get(r.url);
      if (!prev) {
        wanted.set(r.url, { r: { ...r }, decode: d, decodeWidth: dw });
      } else {
        if (r.priority < prev.r.priority) prev.r.priority = r.priority;
        if (d !== null && (prev.decode === null || d < prev.decode)) {
          prev.decode = d;
          prev.decodeWidth = dw;
        }
      }
    }
    for (const entry of [...this.entries.values()]) {
      if (wanted.has(entry.url)) continue;
      entry.wanted = false;
      entry.decodePriority = null;
      if (entry.pins === 0) this.drop(entry);
    }
    for (const { r, decode, decodeWidth } of wanted.values()) {
      let entry = this.entries.get(r.url);
      if (!entry) entry = this.add(r);
      entry.wanted = true;
      entry.priority = r.priority;
      entry.decodePriority = decode;
      entry.decodeWidth = decodeWidth;
      if (decode === null) this.evictBitmap(entry);
    }
    this.pump();
  }

  status(url: string): FrameStatus | null {
    return this.entries.get(url)?.status ?? null;
  }

  /**
   * O carregador reduz ao decodificar (`decodeWidth`)? `null` = ainda não se
   * sabe (descoberto na primeira decodificação reduzida); `false` = os bitmaps
   * vêm no tamanho natural — a janela deve ser dimensionada por ele.
   */
  resizeSupported(): boolean | null {
    return this.resizeOk;
  }

  /** O arquivo já foi baixado (decodificado ou não)? */
  isDownloaded(url: string): boolean {
    const s = this.entries.get(url)?.status;
    return s !== undefined && isDownloaded(s);
  }

  bytes(url: string): number {
    return this.entries.get(url)?.bytes ?? NaN;
  }

  /** Arquivo baixado (comprimido) da URL, se houver — ex.: ler o valor de um pixel no clique. */
  blob(url: string): Blob | null {
    return this.entries.get(url)?.blob ?? null;
  }

  /**
   * Pronta NA LARGURA PEDIDA agora (nenhuma redecodificação pendente)? Uma
   * entrada pronta noutra largura continua em `get()` até a nova chegar; quem
   * exige nitidez uniforme (exportação) espera por isto.
   */
  readyAtRequestedWidth(url: string): boolean {
    const e = this.entries.get(url);
    return !!e && e.status === 'ready' && !e.redecoding && e.decodedFor === e.decodeWidth;
  }

  /** Imagem decodificada da URL, se houver. */
  get(url: string): FrameImage | null {
    const e = this.entries.get(url);
    return e && e.status === 'ready' ? e.image : null;
  }

  /**
   * Melhor imagem DECODIFICADA para (produto, instante): a largura pedida;
   * senão a maior abaixo; senão a menor acima. Com `exact`, só a pedida.
   */
  bestReady(
    productId: string,
    time: number,
    desired: W,
    exact = false,
  ): { url: string; image: FrameImage; width: W } | null {
    const set = this.byFrame.get(frameKey(productId, time));
    if (!set) return null;
    let best: Entry<W> | null = null;
    const score = (w: number) => (w === desired ? 0 : w < desired ? desired - w : 1e6 + w);
    for (const e of set) {
      if (e.status !== 'ready' || !e.image) continue;
      if (exact && e.width !== desired) continue;
      if (!best || score(e.width) < score(best.width)) best = e;
    }
    return best ? { url: best.url, image: best.image!, width: best.width } : null;
  }

  /** Impede que o quadro seja descartado ou tenha o bitmap fechado (está na tela). */
  pin(url: string): void {
    const e = this.entries.get(url);
    if (e) e.pins++;
  }

  unpin(url: string): void {
    const e = this.entries.get(url);
    if (!e || e.pins === 0) return;
    e.pins--;
    if (e.pins > 0) return;
    // Saiu da tela: se já não faz parte do plano (ex.: largura antiga), sai do cache já.
    if (!e.wanted) this.drop(e);
    else if (e.decodePriority === null) this.evictBitmap(e);
  }

  /** Recoloca na fila os quadros que falharam (ex.: após voltar a conexão). */
  retryFailed(): void {
    let changed = false;
    for (const e of this.entries.values()) {
      if (e.status === 'error') {
        e.status = idleStatus(e);
        e.attempts = 0;
        e.retryAt = 0;
        changed = true;
      }
    }
    if (changed) {
      this.notify();
      this.pump();
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  stats(): FrameCacheStats {
    const s: FrameCacheStats = { entries: this.entries.size, downloaded: 0, ready: 0, loading: 0, decoding: 0, bytes: 0, memoryBytes: 0, pixels: 0 };
    for (const e of this.entries.values()) {
      if (isDownloaded(e.status)) {
        s.downloaded++;
        if (Number.isFinite(e.bytes)) s.bytes += e.bytes;
      }
      if (e.blob) s.memoryBytes += e.blob.size;
      if (e.status === 'ready' && e.image) {
        s.ready++;
        s.pixels += e.image.width * e.image.height;
      } else if (e.status === 'loading') s.loading++;
      else if (e.status === 'decoding') s.decoding++;
    }
    return s;
  }

  /** Memória por largura (contagem, bytes comprimidos, pixels decodificados). */
  statsByWidth(): Record<number, WidthStats> {
    const out: Record<number, WidthStats> = {};
    for (const e of this.entries.values()) {
      const w = (out[e.width] ??= { count: 0, downloaded: 0, bytes: 0, decoded: 0, pixels: 0 });
      w.count++;
      if (isDownloaded(e.status)) {
        w.downloaded++;
        if (Number.isFinite(e.bytes)) w.bytes += e.bytes;
      }
      if (e.status === 'ready' && e.image) {
        w.decoded++;
        w.pixels += e.image.width * e.image.height;
      }
    }
    return out;
  }

  /** Descarta tudo o que não está na tela. */
  clear(): void {
    for (const e of [...this.entries.values()]) if (e.pins === 0) this.drop(e);
  }

  // -------------------------------------------------------------------------

  private add(r: FrameRequest<W>): Entry<W> {
    const entry: Entry<W> = {
      url: r.url,
      productId: r.productId,
      time: r.time,
      width: r.width,
      priority: r.priority,
      decodePriority: null,
      status: 'queued',
      blob: null,
      onDisk: false,
      image: null,
      bytes: NaN,
      controller: null,
      attempts: 0,
      retryAt: 0,
      pins: 0,
      wanted: true,
      decodeWidth: null,
      decodedFor: null,
      redecoding: false,
      decodeSeq: 0,
    };
    this.entries.set(r.url, entry);
    const key = frameKey(r.productId, r.time);
    let set = this.byFrame.get(key);
    if (!set) {
      set = new Set();
      this.byFrame.set(key, set);
    }
    set.add(entry);
    return entry;
  }

  /** Fecha o bitmap (volta a "downloaded"), se não estiver na tela. */
  private evictBitmap(entry: Entry<W>): void {
    if (entry.pins > 0 || entry.status !== 'ready') return;
    releaseImage(entry.image);
    entry.image = null;
    entry.decodedFor = null;
    entry.status = idleStatus(entry);
  }

  /** Está pronto, mas noutra largura de bitmap que a pedida agora (ex.: zoom mudou)? */
  private needsRedecode(e: Entry<W>): boolean {
    return e.status === 'ready' && e.decodePriority !== null && !e.redecoding && e.decodedFor !== e.decodeWidth;
  }

  /**
   * Tira o quadro do cache (RAM). O arquivo no DISCO fica: quem decide o que
   * sai de lá é a faxina (`frameStorage.ts`), que sabe o que ainda vale guardar.
   */
  private drop(entry: Entry<W>): void {
    entry.controller?.abort();
    releaseImage(entry.image);
    entry.image = null;
    entry.blob = null;
    this.entries.delete(entry.url);
    const key = frameKey(entry.productId, entry.time);
    const set = this.byFrame.get(key);
    set?.delete(entry);
    if (set && set.size === 0) this.byFrame.delete(key);
  }

  private pump(): void {
    const now = Date.now();
    let nextRetry = Infinity;
    while (this.activeDownloads < this.maxDownloads) {
      let best: Entry<W> | null = null;
      for (const e of this.entries.values()) {
        if (e.status !== 'queued') continue;
        if (e.retryAt > now) {
          nextRetry = Math.min(nextRetry, e.retryAt);
          continue;
        }
        // Quadros da janela primeiro; depois a ordem de download.
        if (!best || rank(e) < rank(best)) best = e;
      }
      if (!best) break;
      void this.download(best);
    }
    while (this.activeDecodes < this.maxDecodes) {
      let best: Entry<W> | null = null;
      for (const e of this.entries.values()) {
        if (e.decodePriority === null) continue;
        if (e.status !== 'downloaded' && !this.needsRedecode(e)) continue;
        if (!best || e.decodePriority < best.decodePriority!) best = e;
      }
      if (!best) break;
      void this.decode(best);
    }
    if (nextRetry !== Infinity && !this.retryTimer) {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.pump();
      }, nextRetry - now + 10);
    }
  }

  private async download(entry: Entry<W>): Promise<void> {
    entry.status = 'loading';
    entry.attempts++;
    const controller = new AbortController();
    entry.controller = controller;
    this.activeDownloads++;
    try {
      const store = this.store;
      if (store) {
        // Já está no disco (desta visita ou da anterior)? Então não vai à rede.
        const kept = await store.get(entry.url).catch(() => null);
        if (this.entries.get(entry.url) !== entry || controller.signal.aborted) return;
        if (kept) {
          entry.bytes = kept.size;
          entry.onDisk = true;
          entry.status = 'downloaded';
          return;
        }
      }
      const blob = await this.loader.download(entry.url, controller.signal);
      if (this.entries.get(entry.url) !== entry) return;
      entry.blob = blob;
      entry.bytes = blob.size;
      entry.status = 'downloaded';
      if (store) void this.persist(entry, store, blob);
    } catch (e) {
      if (this.entries.get(entry.url) !== entry || isAbortError(e)) return;
      // 404/403…: o arquivo não existe (não adianta insistir agora).
      const permanent = e instanceof HttpError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429;
      if (!permanent && entry.attempts < MAX_ATTEMPTS) {
        entry.status = 'queued';
        entry.retryAt = Date.now() + RETRY_DELAYS[Math.min(entry.attempts - 1, RETRY_DELAYS.length - 1)];
      } else {
        entry.status = 'error';
      }
    } finally {
      entry.controller = null;
      this.activeDownloads--;
      this.notify();
      this.pump();
    }
  }

  /**
   * Grava no disco e solta o arquivo da RAM. Sem espaço, o arquivo fica na
   * RAM e `onStoreFull` avisa quem dimensiona o loop.
   */
  private async persist(entry: Entry<W>, store: BlobStore, blob: Blob): Promise<void> {
    try {
      await store.put(entry.url, blob);
    } catch (e) {
      this.onStoreFull?.(e);
      return;
    }
    // Saiu do cache enquanto gravava: a faxina do disco decide se fica.
    if (this.entries.get(entry.url) !== entry) return;
    entry.onDisk = true;
    // Uma decodificação em andamento já tem a referência; as próximas releem do disco.
    entry.blob = null;
    this.notify();
  }

  private async decode(entry: Entry<W>): Promise<void> {
    if (!entry.blob && !(entry.onDisk && this.store)) {
      // Sem o arquivo em lugar nenhum (o depósito foi desligado): baixa de novo.
      // Mudar o estado aqui é o que impede `pump` de escolher este quadro sem fim.
      entry.onDisk = false;
      if (entry.status === 'ready') entry.decodeWidth = entry.decodedFor;
      else entry.status = 'queued';
      return;
    }
    // Redecodificação (outra largura de bitmap): a imagem atual continua na
    // tela e em `get()` até a nova estar pronta — nunca um quadro vazio.
    const redecode = entry.status === 'ready';
    if (redecode) entry.redecoding = true;
    else entry.status = 'decoding';
    const want = entry.decodeWidth;
    const seq = ++entry.decodeSeq;
    this.activeDecodes++;
    try {
      const blob = entry.blob ?? (await this.store!.get(entry.url).catch(() => null));
      if (this.entries.get(entry.url) !== entry || entry.decodeSeq !== seq) return;
      if (!blob) {
        // O navegador apagou o arquivo do disco (falta de espaço): baixa de novo.
        entry.onDisk = false;
        if (redecode) entry.decodeWidth = entry.decodedFor;
        else {
          entry.status = 'queued';
          entry.attempts = 0;
        }
        return;
      }
      const image = await this.loader.decode(blob, want, { url: entry.url, productId: entry.productId });
      if (this.entries.get(entry.url) !== entry || entry.decodeSeq !== seq) {
        releaseImage(image);
        return;
      }
      let decodedFor = want;
      if (want !== null && image.width !== want) {
        // Navegador sem redução: veio no tamanho natural; não pedimos mais.
        this.resizeOk = false;
        decodedFor = null;
      } else if (want !== null && this.resizeOk === null) {
        this.resizeOk = true;
      }
      if (entry.decodePriority === null && entry.pins === 0) {
        // Saiu da janela enquanto decodificava.
        releaseImage(image);
        if (entry.status === 'decoding') entry.status = idleStatus(entry);
        return;
      }
      releaseImage(entry.image);
      entry.image = image;
      entry.decodedFor = decodedFor;
      if (this.resizeOk === false) entry.decodeWidth = null;
      entry.status = 'ready';
    } catch {
      if (this.entries.get(entry.url) !== entry || entry.decodeSeq !== seq) return;
      if (redecode) {
        // Já tinha decodificado antes: fica com a imagem atual, sem insistir.
        entry.decodeWidth = entry.decodedFor;
        return;
      }
      entry.status = 'error';
      entry.blob = null;
      // Arquivo que não decodifica não fica no disco: "tentar de novo" baixa outra vez.
      if (entry.onDisk) {
        entry.onDisk = false;
        void this.store?.delete(entry.url).catch(() => {});
      }
    } finally {
      if (redecode) entry.redecoding = false;
      this.activeDecodes--;
      this.notify();
      this.pump();
    }
  }

  private notify(): void {
    if (this.notifyQueued) return;
    this.notifyQueued = true;
    queueMicrotask(() => {
      this.notifyQueued = false;
      for (const l of this.listeners) l();
    });
  }
}

/** Ordem de download: quadros da janela de decodificação na frente dos demais. */
function rank(e: Entry<number>): number {
  return e.decodePriority !== null ? e.decodePriority - 1e6 : e.priority;
}

/** Instância única usada pelo app: uma decodificação a mais que os workers, para nenhum ficar ocioso. */
export const frameCache = new FrameCache(6, DECODE_WORKERS + 1);
